import { useState, useCallback, useMemo } from 'react';
import type { ChatResponse, EntityDetails, HistoryItem } from './api';

/**
 * The active client context.
 *
 * Source-agnostic by design: it holds a client id plus point-in-time display
 * fields and is indifferent to where the client came from. Today it is set
 * from a history entry ("Search for {client}'s information"); the future
 * Disambiguation story reuses this same container rather than reimplementing
 * one. `id` is the source of truth (sent as `current_client_id` on /chat) so
 * two same-named clients are never conflated.
 */
export interface ClientContext extends EntityDetails {
  id: string;
}

export interface ClientContextStore {
  /** The active context, or null when none is set (fresh, context-free thread). */
  context: ClientContext | null;
  /** Client id to send as `current_client_id`, or undefined when no context. */
  currentClientId: string | undefined;
  /** Set the active context (source-agnostic). */
  set: (context: ClientContext) => void;
  /** Clear the active context. */
  reset: () => void;
}

/**
 * Builds a {@link ClientContext} from a history item's stored id + snapshot.
 * Returns null when the item has no `entityId` (should not happen for items
 * returned by GET /history, which are entity-scoped, but guarded anyway).
 */
export function contextFromHistoryItem(
  item: HistoryItem,
): ClientContext | null {
  if (!item.entityId) return null;
  return {
    id: item.entityId,
    firstName: item.entityDetails?.firstName,
    lastName: item.entityDetails?.lastName,
    preferredName: item.entityDetails?.preferredName,
    dateOfBirth: item.entityDetails?.dateOfBirth,
    clientAdviserName: item.entityDetails?.clientAdviserName,
    preferredSuburb: item.entityDetails?.preferredSuburb,
    preferredState: item.entityDetails?.preferredState,
  };
}

/**
 * Builds a {@link ClientContext} from a `/chat` response's resolved entity.
 * Twin of {@link contextFromHistoryItem}: lets the UI pin the context tag from
 * a chat turn using the same container. Returns null when the turn did not
 * resolve to a single client (`entityId` absent), so a non-single-client turn
 * (zero / multiple / conflict / disambiguation / system error) never sets a tag.
 */
export function contextFromChatResponse(
  resp: ChatResponse,
): ClientContext | null {
  if (!resp.entityId) return null;
  return {
    id: resp.entityId,
    firstName: resp.entityDetails?.firstName,
    lastName: resp.entityDetails?.lastName,
    preferredName: resp.entityDetails?.preferredName,
    dateOfBirth: resp.entityDetails?.dateOfBirth,
    clientAdviserName: resp.entityDetails?.clientAdviserName,
    preferredSuburb: resp.entityDetails?.preferredSuburb,
    preferredState: resp.entityDetails?.preferredState,
  };
}

/**
 * Builds a {@link ClientContext} from an auto-detected client id plus the
 * display snapshot fetched from `GET /client/{id}`. Third producer alongside
 * {@link contextFromHistoryItem} and {@link contextFromChatResponse}: the
 * `id` (detected from the XPLAN URL at Porter launch) is the source of truth;
 * `details` fill the "Searching for" tag and may be null (a missing snapshot
 * still yields a valid context on the id alone — `current_client_id` needs
 * only the id).
 */
export function contextFromClientDetails(
  id: string,
  details: EntityDetails | null,
): ClientContext {
  return {
    id,
    firstName: details?.firstName,
    lastName: details?.lastName,
    preferredName: details?.preferredName,
    dateOfBirth: details?.dateOfBirth,
    clientAdviserName: details?.clientAdviserName,
    preferredSuburb: details?.preferredSuburb,
    preferredState: details?.preferredState,
  };
}

/**
 * Hook owning the active client-context state with source-agnostic
 * `set` / `reset` operations.
 */
export function useClientContext(): ClientContextStore {
  const [context, setContext] = useState<ClientContext | null>(null);

  const set = useCallback((next: ClientContext) => {
    setContext(next);
  }, []);

  const reset = useCallback(() => {
    setContext(null);
  }, []);

  return useMemo(
    () => ({
      context,
      currentClientId: context?.id,
      set,
      reset,
    }),
    [context, set, reset],
  );
}
